/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import './currencyStyle.css';
import { useQuery } from 'react-query';
import legalEntityCurrencyService from 'service/legalEntityCurrency';
import uzFlag from 'assets/img/icons/uz_flag.png';
import ruFlag from 'assets/img/icons/ru_flag.png';
import usFlag from 'assets/img/icons/us_flag.png';

interface CurrencySelectorProps {
  onCurrencyChange?: (currency: string) => void;
  selectedCurrency?: any;
  disabled?: boolean;
  className?: string;
}

// Currency Selector Component
const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  onCurrencyChange,
  selectedCurrency,
  disabled = false,
  className = ''
}) => {
  const { i18n } = useTranslation();
  const [currency, setCurrency] = useState<string>('uzs');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [currencyOption, setCurrencyOption] = useState<any>([]);

  const flagMap: { [key: number]: string } = {
    2: uzFlag,
    1: usFlag,
    3: ruFlag
  };

  //GET Currency to options
  useQuery(['GET_CURRENCY'], async () => {
    await legalEntityCurrencyService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.legal_entity_currencies?.map((option: any) => ({
          value: option?.currency?.id,
          name: option?.currency?.name?.[i18n?.language],
          code: option?.currency?.char_code,
          pid: option?.currency?.pid || 0
        }));
        setCurrencyOption(options);
      });
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setCurrency(selectedCurrency?.code || 'uzs');
  }, [selectedCurrency]);

  const handleCurrencySelect = (currencyCode: any): void => {
    setCurrency(currencyCode);
    setIsOpen(false);
    if (onCurrencyChange) {
      onCurrencyChange(currencyCode);
    }
  };

  const handleToggleClick = (): void => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleClick();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const selectedCurrencyData = currencyOption?.find(
    (c: any) => c.code === currency
  );

  if (!selectedCurrencyData) {
    console.warn(`Currency ${currency} not found in currencies list`);
  }

  const selectedFlag = selectedCurrencyData?.pid
    ? flagMap[selectedCurrencyData.pid]
    : uzFlag;

  return (
    <div
      className={`main-dropdown currency-dropdown ${className}`}
      ref={dropdownRef}
    >
      <button
        className={`currency-btn ${disabled ? 'disabled' : ''}`}
        type="button"
        onClick={handleToggleClick}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        disabled={disabled}
      >
        <div className="currency-display">
          <img
            src={selectedFlag}
            alt={`${selectedCurrencyData?.name || 'Currency'} flag`}
            className="currency-flag"
            role="img"
          />
        </div>
      </button>

      <ul
        /* prettier-ignore */
        className={`currency-dropdown-menu currency-menu ${
          isOpen ? 'show' : ''
        }`}
        role="menu"
      >
        {currencyOption?.map((curr: any) => (
          <li key={curr.code} role="none">
            <button
              /* prettier-ignore */
              className={`currency-dropdown-item currency-item ${
                currency === curr.code ? 'active' : ''
              }`}
              onClick={() => handleCurrencySelect(curr)}
              role="menuitem"
              type="button"
            >
              <div className="currency-item-content">
                <div className="currency-info">
                  <span className="currency-code">{curr.name}</span>
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CurrencySelector;
